/* eslint-disable react/display-name */
import React, { Suspense, lazy } from 'react';
import qs from 'qs';
import { Redirect } from 'react-router';
import PAGE_TYPES from 'tradera-constants/page-types';
import { SUPPORTED_LANGUAGES } from 'tradera-utils/url';
import { NotFoundError } from 'tradera-components/errors';
import Loader from 'tradera-components/loader';
import StartPage from './pages/start-page'; // Startpage is included for speed
import CartPage from './pages/cart-page';
import { RouteMapRoutes } from 'tradera-components/route-map-routes/route-map-routes';
import { PageMeta } from 'tradera-components/page-meta/page-meta';
import GtmService from 'static/script/app/ui/google-tagmanager-service';

// Lacy load other pages
const BrandPage = lazy(() =>
    import(
        /* webpackChunkName: "brand-page" */ 'tradera-apps/tradera/pages/brand-page'
    )
);
const BrandListPage = lazy(() =>
    import(
        /* webpackChunkName: "brand-list-page" */ 'tradera-apps/tradera/pages/brand-list-page'
    )
);
const CampaignPage = lazy(() =>
    import(
        /* webpackChunkName: "campaign-page" */ 'tradera-apps/tradera/pages/campaign-page'
    )
);
const CategoryListPage = lazy(() =>
    import(
        /* webpackChunkName: "category-list-page" */ 'tradera-apps/tradera/pages/category-list-page'
    )
);
const CategoryPage = lazy(() =>
    import(
        /* webpackChunkName: "category-page" */ 'tradera-apps/tradera/pages/category-page'
    )
);
const CheckoutPage = lazy(() =>
    import(
        /* webpackChunkName: "checkout-page" */ 'tradera-apps/tradera/pages/checkout-page'
    )
);
const CheckoutConfirmationPage = lazy(() =>
    import(
        /* webpackChunkName: "checkout-confirmation-page" */ 'tradera-apps/tradera/pages/checkout-confirmation-page'
    )
);
const FavouriteSellersPage = lazy(() =>
    import(
        /* webpackChunkName: "favourite-sellers" */ 'tradera-apps/tradera/pages/favourite-sellers-page'
    )
);
const Landing_GantXTradera = lazy(() =>
    import(
        /* webpackChunkName: "landing_gantxtradera" */ 'tradera-apps/tradera/pages/landing/gant-x-tradera'
    )
);
const Landing_StadiumXTradera = lazy(() =>
    import(
        /* webpackChunkName: "landing-stadiumxtradera" */ 'tradera-apps/tradera/pages/landing/stadium-x-tradera'
    )
);
const ProfilePage = lazy(() =>
    import(
        /* webpackChunkName: "profile-page" */ 'tradera-apps/tradera/pages/profile-page'
    )
);
const ShippingInfoPage = lazy(() =>
    import(
        /* webpackChunkName: "shippingcalculator" */ 'tradera-apps/tradera/pages/shipping-info-page'
    )
);
const CombinedShippingInfoPage = lazy(() =>
    import(
        /* webpackChunkName: "combined-shipping" */ 'tradera-apps/tradera/pages/combined-shipping-info'
    )
);
const RecommendationsPage = lazy(() =>
    import(
        /* webpackChunkName: "profile-page" */ 'tradera-apps/tradera/pages/recommendations-page'
    )
);
const ReportItemPage = lazy(() =>
    import(
        /* webpackChunkName: "report-item-page" */ 'tradera-apps/tradera/pages/report-item-page'
    )
);
const SearchPage = lazy(() =>
    import(
        /* webpackChunkName: "search-page" */ 'tradera-apps/tradera/pages/search-page'
    )
);
const TraderaShopPage = lazy(() =>
    import(
        /* webpackChunkName: "tradera-shop" */ 'tradera-apps/tradera/pages/tradera-shop-page'
    )
);
const VerifyPage = lazy(() =>
    import(
        /* webpackChunkName: "verify-page" */ 'tradera-apps/tradera/pages/verify-page'
    )
);
const LiveShoppingListPage = lazy(() =>
    import(
        /* webpackChunkName: "live-shopping-list-page" */ 'tradera-apps/tradera/pages/live-shopping-list-page'
    )
);

const LiveShoppingDetailsPage = lazy(() =>
    import(
        /* webpackChunkName: "live-shopping-details-page" */ 'tradera-apps/tradera/pages/live-shopping-details-page'
    )
);

const LiveShoppingSignUpPage = lazy(() =>
    import(
        /* webpackChunkName: "live-shopping-sign-up-page" */ 'tradera-apps/tradera/pages/live-shopping-sign-up-page'
    )
);

const ViewItemPage = lazy(() =>
    import(
        /* webpackChunkName: "view-item-page" */ 'tradera-apps/tradera/pages/view-item-page'
    )
);

const UPI = lazy(() =>
    import(/* webpackChunkName: "upi" */ 'tradera-apps/tradera/pages/upi')
);

const INR = lazy(() =>
    import(/* webpackChunkName: "inr" */ 'tradera-apps/tradera/pages/inr')
);

const Cause = lazy(() =>
    import(/* webpackChunkName: "cause" */ 'tradera-apps/tradera/pages/cause')
);

const Causes = lazy(() =>
    import(/* webpackChunkName: "causes" */ 'tradera-apps/tradera/pages/causes')
);

const CausesInfo = lazy(() =>
    import(
        /* webpackChunkName: "causes-info" */ 'tradera-apps/tradera/pages/causes-info'
    )
);

export const OPTIONAL_LANG = `/:lang(${SUPPORTED_LANGUAGES.join('|')})?`;

export const routeMap = {
    startPage: {
        path: [`${OPTIONAL_LANG}/`, `${OPTIONAL_LANG}`],
        pageType: PAGE_TYPES.START_PAGE,
        strict: true,
        exact: true,
        onLoaded: () => {
            GtmService.trackGtmEvent('viewHomePage');
        },
        render: props => <StartPage {...props} />
    },
    viewItemPage: {
        path: `${OPTIONAL_LANG}/item/:categoryId/:itemId/:itemSlug`,
        pageType: PAGE_TYPES.VIEW_ITEM_PAGE,
        mustLoadDataBeforePageTracking: true,
        render: props => <ViewItemPage {...props} />
    },
    traderaShopPage: {
        path: '/:lang(en|de|da)?/tradera-shop/:urlSlug',
        pageType: PAGE_TYPES.TRADERA_SHOP_PAGE,
        render: props => <TraderaShopPage {...props} />
    },
    causesInfoPage: {
        path: `${OPTIONAL_LANG}/charity/how-to-become`,
        pageType: PAGE_TYPES.DEFAULT,
        render: props => <CausesInfo {...props} />
    },
    causesPage: {
        path: `${OPTIONAL_LANG}/causes`,
        pageType: PAGE_TYPES.DEFAULT,
        render: props => <Causes {...props} />
    },
    causePage: {
        path: `${OPTIONAL_LANG}/cause/:causeSlug`,
        pageType: PAGE_TYPES.CUSTOM_RESULT_PAGE,
        mustLoadDataBeforePageTracking: true,
        render: props => <Cause {...props} />
    },
    upiPage: {
        path: `${OPTIONAL_LANG}/resolution/UPI2/:purchaseOrderId`,
        pageType: PAGE_TYPES.DEFAULT,
        render: props => <UPI {...props} />
    },
    inrPage: {
        path: `${OPTIONAL_LANG}/resolution/INR2/:purchaseOrderId`,
        pageType: PAGE_TYPES.DEFAULT,
        render: props => <INR {...props} />
    },
    brandPage: {
        path: `${OPTIONAL_LANG}/brand/:brand`,
        pageType: PAGE_TYPES.BRAND_PAGE,
        render: props => {
            const brand = props.match.params.brand;
            return <BrandPage brand={brand} />;
        }
    },
    brandListPage: {
        path: `${OPTIONAL_LANG}/brands/`,
        pageType: PAGE_TYPES.BRAND_LIST_PAGE,
        render: props => <BrandListPage {...props} />
    },
    campaignPage: {
        path: [
            `${OPTIONAL_LANG}/campaign/:campaignOrCharityId`,
            `${OPTIONAL_LANG}/charity/:campaignOrCharityId`
        ],
        pageType: PAGE_TYPES.CUSTOM_RESULT_PAGE,
        mustLoadDataBeforePageTracking: true,
        render: props => <CampaignPage {...props} />
    },
    categoryListPage: {
        path: `${OPTIONAL_LANG}/kategorier`,
        pageType: PAGE_TYPES.CATEGORY_LIST,
        render: props => <CategoryListPage {...props} />
    },
    checkoutConfirmationPage: {
        path: `${OPTIONAL_LANG}/shopping/ordercheckout/confirmation/:purchaseOrderId`,
        pageType: PAGE_TYPES.DEFAULT,
        render: props => <CheckoutConfirmationPage {...props} />
    },
    checkoutPage: {
        path: `${OPTIONAL_LANG}/shopping/ordercheckout/:purchaseOrderId`,
        pageType: PAGE_TYPES.DEFAULT,
        render: props => (
            <CheckoutPage
                preSelectPayment={
                    qs.parse(props.location.search, {
                        ignoreQueryPrefix: true
                    }).payment
                }
                preSelectShipping={
                    qs.parse(props.location.search, {
                        ignoreQueryPrefix: true
                    }).shipping
                }
                {...props}
            />
        )
    },
    cartPage: {
        path: `${OPTIONAL_LANG}/shopping/cart`,
        pageType: PAGE_TYPES.DEFAULT,
        render: props => <CartPage {...props} />
    },
    searchResultPage: {
        path: `${OPTIONAL_LANG}/search`,
        pageType: PAGE_TYPES.SEARCH_RESULT_PAGE,
        mustLoadDataBeforePageTracking: true,
        onLoaded: getState => {
            const { itemsById, queryParams } = getState().discover;
            GtmService.trackGtmEvent('search', {
                searchTerm: queryParams.q,
                itemsById
            });
        },
        render: props => <SearchPage {...props} />
    },
    favouriteSellersPage: {
        path: '/:lang(sv|en|de|da)?/favourite-sellers',
        pageType: PAGE_TYPES.FAVOURITE_SELLERS_PAGE,
        render: props => <FavouriteSellersPage {...props} />
    },
    landing_GantXTradera: {
        path: `${OPTIONAL_LANG}/gantxtradera`,
        pageType: PAGE_TYPES.DEFAULT,
        render: props => <Landing_GantXTradera {...props} />
    },
    landing_StadiumXTradera: {
        path: `${OPTIONAL_LANG}/stadiumxtradera`,
        pageType: PAGE_TYPES.DEFAULT,
        render: props => <Landing_StadiumXTradera {...props} />
    },
    liveShoppingSignUpPage: {
        path: '/:lang(en|de|da)?/live/sign-up',
        pageType: PAGE_TYPES.LIVE_SHOPPING,
        exact: true,
        render: props => <LiveShoppingSignUpPage {...props} />
    },
    liveShoppingDetailsPage: {
        path: '/:lang(en|de|da)?/live/:liveId',
        pageType: PAGE_TYPES.LIVE_SHOPPING,
        render: props => <LiveShoppingDetailsPage {...props} />
    },
    liveShoppingListPage: {
        path: '/:lang(en|de|da)?/live',
        pageType: PAGE_TYPES.LIVE_SHOPPING,
        render: props => <LiveShoppingListPage {...props} />
    },
    profilePage: {
        path: `${OPTIONAL_LANG}/profile/:profilePageType(items|feedback|information)/:memberId/:aliasSlug`,
        pageType: PAGE_TYPES.PROFILE_PAGE,
        mustLoadDataBeforePageTracking: true,
        render: props => <ProfilePage {...props} />
    },
    recommendationsPage: {
        path: `${OPTIONAL_LANG}/ma/endless-recommendation/:recommendationModeId(selected|endingSoon)`,
        pageType: PAGE_TYPES.ENDLESS_RECOMMENDATIONS_PAGE,
        render: props => <RecommendationsPage {...props} />
    },
    reportItemPage: {
        path: `${OPTIONAL_LANG}/report-item/:itemId`,
        pageType: PAGE_TYPES.REPORT_ITEM_PAGE,
        render: props => <ReportItemPage {...props} />
    },
    verifyPage: {
        path: `${OPTIONAL_LANG}/verify/bankid`,
        pageType: PAGE_TYPES.DEFAULT,
        render: props => (
            <VerifyPage
                // TODO: Move into VerifyPage component
                returnUrl={
                    qs.parse(props.location.search, {
                        ignoreQueryPrefix: true
                    }).returnUrl
                }
                {...props}
            />
        )
    },
    CombinedShippingInfoPage: {
        path: `${OPTIONAL_LANG}/frakt/samfrakt`,
        pageType: PAGE_TYPES.DEFAULT,
        render: props => <CombinedShippingInfoPage {...props} />
    },
    ShippingInfoPage: {
        path: `${OPTIONAL_LANG}/frakt`,
        pageType: PAGE_TYPES.DEFAULT,
        render: props => <ShippingInfoPage {...props} />
    },
    categoryResultPage: {
        path: [`${OPTIONAL_LANG}/category/:categoryId`],
        pageType: PAGE_TYPES.CATEGORY_RESULT_PAGE,
        exact: true,
        mustLoadDataBeforePageTracking: true,
        render: props => <CategoryPage {...props} />
    },
    legacyCategoryResultPage: {
        path: [`${OPTIONAL_LANG}/:categoryNameAndId`],
        pageType: PAGE_TYPES.CATEGORY_RESULT_PAGE,
        exact: true,
        render: props => {
            const s = props.match.params.categoryNameAndId;
            const divider = s.indexOf('_') > -1 ? '_' : '-';
            const parts = s.split(divider);
            const categoryId = parts.pop();
            return (
                <Redirect
                    to={`/category/${categoryId}${props.location.search}`}
                />
            );
        }
    },
    notFoundPage: {
        path: '*',
        pageType: PAGE_TYPES.ERROR_PAGE,
        exact: true,
        render: () => <NotFoundError />
    }
};

const SuspenseFallback = () => {
    window.scrollTo(0, 0);
    return <Loader usePortal={false} />;
};

const Routes = () => {
    return (
        <Suspense fallback={<SuspenseFallback />}>
            <PageMeta routeMap={routeMap} />
            <RouteMapRoutes routeMap={routeMap} />
        </Suspense>
    );
};

export default Routes;
